import { useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import styles from './ChatInput.module.css'

export type ChatInputProps = {
  onSubmit: (query: string) => void
  disabled?: boolean
}

export default function ChatInput({ onSubmit, disabled = false }: ChatInputProps) {
  const [query, setQuery] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow with content: collapse to measure, then take the scroll height.
  // CSS min/max-height clamp the result to the 3–10 line band; past the max
  // the textarea scrolls internally. Manual mouse-resize is disabled in CSS.
  // +2px covers the top/bottom borders (box-sizing: border-box) so the
  // measured content never triggers a phantom scrollbar.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }, [query])

  const submit = (): void => {
    if (disabled) return
    const trimmed = query.trim()
    if (trimmed === '') return
    onSubmit(trimmed)
    setQuery('')
  }

  const handle = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    submit()
  }

  // Enter sends the message; Shift+Enter inserts a newline — the Teams /
  // ChatGPT convention. We ignore the keypress while other modifiers are
  // held so OS-level shortcuts aren't swallowed.
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key !== 'Enter') return
    if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return
    e.preventDefault()
    submit()
  }

  return (
    <form className={styles.form} onSubmit={handle}>
      <textarea
        ref={textareaRef}
        className={styles.input}
        rows={3}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Still et spørsmål..."
        aria-label="Spørsmål"
        disabled={disabled}
      />
      <button className={styles.submit} type="submit" disabled={disabled || query.trim() === ''}>
        Send
      </button>
      <p className={styles.hint}>Enter for å sende · Shift+Enter for linjeskift</p>
    </form>
  )
}
